import type {
  AttributeType,
  MappingAttribute,
  MappingConfiguration,
  ObjectTypeMapping,
  PipingSystemProperty,
  ProjectProperty,
} from '../types';

const ATTRIBUTE_TYPES: ReadonlyArray<AttributeType> = [
  'Parameter',
  'BomColumn',
  'Attribute',
  'Formula',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isAttributeType = (value: unknown): value is AttributeType =>
  typeof value === 'string' && ATTRIBUTE_TYPES.includes(value as AttributeType);

const sanitizeSimpleProperties = <T extends PipingSystemProperty | ProjectProperty>(
  value: unknown
): T[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  const sanitized = value
    .map((entry) => {
      if (!isRecord(entry)) {
        return null;
      }

      const id = typeof entry.id === 'string' ? entry.id : '';
      const name = typeof entry.name === 'string' ? entry.name : '';
      const rawValue = entry.value;
      const propertyValue =
        typeof rawValue === 'string' || typeof rawValue === 'number'
          ? String(rawValue)
          : '';

      if (!id || !name) {
        return null;
      }

      return {
        id,
        name,
        value: propertyValue,
      } as T;
    })
    .filter((entry): entry is T => entry !== null);

  return sanitized.length > 0 ? sanitized : null;
};

const sanitizeMappingAttribute = (
  value: unknown,
  fallbackOrder: number
): MappingAttribute | null => {
  if (!isRecord(value)) {
    return null;
  }

  const pcfAttributeName =
    typeof value.pcfAttributeName === 'string' ? value.pcfAttributeName : '';
  if (!pcfAttributeName) {
    return null;
  }

  return {
    order:
      typeof value.order === 'number' && Number.isFinite(value.order)
        ? value.order
        : fallbackOrder,
    pcfAttributeName,
    revitParam: typeof value.revitParam === 'string' ? value.revitParam : '',
    attributeType: isAttributeType(value.attributeType)
      ? value.attributeType
      : 'Parameter',
    fx: typeof value.fx === 'string' ? value.fx : '',
  };
};

const sanitizeObjectTypeMapping = (value: unknown): ObjectTypeMapping | null => {
  if (!isRecord(value)) {
    return null;
  }

  const name = typeof value.name === 'string' ? value.name : '';
  if (!name || !Array.isArray(value.attributes)) {
    return null;
  }

  const attributes = value.attributes
    .map((attribute, index) => sanitizeMappingAttribute(attribute, index + 1))
    .filter((attribute): attribute is MappingAttribute => attribute !== null);

  return {
    name,
    attributes,
  };
};

const mergeObjectTypes = (
  persistedObjectTypes: unknown,
  fallbackObjectTypes: ObjectTypeMapping[]
): ObjectTypeMapping[] => {
  if (!Array.isArray(persistedObjectTypes)) {
    return fallbackObjectTypes;
  }

  const sanitizedPersisted = persistedObjectTypes
    .map((entry) => sanitizeObjectTypeMapping(entry))
    .filter((entry): entry is ObjectTypeMapping => entry !== null);

  if (sanitizedPersisted.length === 0) {
    return fallbackObjectTypes;
  }

  const persistedNames = new Set(sanitizedPersisted.map((entry) => entry.name));
  const missingFallback = fallbackObjectTypes.filter(
    (entry) => !persistedNames.has(entry.name)
  );

  return [...sanitizedPersisted, ...missingFallback];
};

export const mergeMappingConfiguration = (
  persistedMappingConfiguration: unknown,
  fallbackMappingConfiguration: MappingConfiguration
): MappingConfiguration => {
  if (!isRecord(persistedMappingConfiguration)) {
    return fallbackMappingConfiguration;
  }

  const persistedGeneral = isRecord(persistedMappingConfiguration.general)
    ? persistedMappingConfiguration.general
    : undefined;
  const persistedMappingTable =
    persistedGeneral && isRecord(persistedGeneral.mappingTable)
      ? persistedGeneral.mappingTable
      : undefined;
  const persistedAdvanced = isRecord(persistedMappingConfiguration.advanced)
    ? persistedMappingConfiguration.advanced
    : undefined;
  const persistedActions = isRecord(persistedMappingConfiguration.actions)
    ? persistedMappingConfiguration.actions
    : undefined;

  const pipingSystemProperties = sanitizeSimpleProperties<PipingSystemProperty>(
    persistedGeneral?.pipingSystemProperties
  );
  const projectProperties = sanitizeSimpleProperties<ProjectProperty>(
    persistedGeneral?.projectProperties
  );

  return {
    ...fallbackMappingConfiguration,
    general: {
      ...fallbackMappingConfiguration.general,
      mappingTable: {
        ...fallbackMappingConfiguration.general.mappingTable,
        addButton:
          typeof persistedMappingTable?.addButton === 'string'
            ? persistedMappingTable.addButton
            : fallbackMappingConfiguration.general.mappingTable.addButton,
        objectType: mergeObjectTypes(
          persistedMappingTable?.objectType,
          fallbackMappingConfiguration.general.mappingTable.objectType
        ),
      },
      pipingSystemProperties:
        pipingSystemProperties ??
        fallbackMappingConfiguration.general.pipingSystemProperties,
      projectProperties:
        projectProperties ?? fallbackMappingConfiguration.general.projectProperties,
    },
    advanced: {
      ...fallbackMappingConfiguration.advanced,
      centerOfCoordinates:
        persistedAdvanced?.centerOfCoordinates === 'SurveyPoint' ||
        persistedAdvanced?.centerOfCoordinates === 'ProjectBasePoint'
          ? persistedAdvanced.centerOfCoordinates
          : fallbackMappingConfiguration.advanced.centerOfCoordinates,
    },
    actions: {
      ...fallbackMappingConfiguration.actions,
      saveSetting:
        typeof persistedActions?.saveSetting === 'string'
          ? persistedActions.saveSetting
          : fallbackMappingConfiguration.actions.saveSetting,
      cancel:
        typeof persistedActions?.cancel === 'string'
          ? persistedActions.cancel
          : fallbackMappingConfiguration.actions.cancel,
    },
  };
};
